<template>
  <div>
    <v-toolbar color="primary" dark tabs flat dense class="view-toolbar">
      <v-tooltip v-if="!drawerState" bottom>
        <template #activator="{ on }">
          <v-app-bar-nav-icon @click="$emit('toggle-drawer')" v-on="on"></v-app-bar-nav-icon>
        </template>
        <span>Open side menu</span>
      </v-tooltip>
      <v-toolbar-title>
        {{ toolbarTitle }}
        <WikiButton v-if="wikiSection" :section="wikiSection" />
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-icon v-if="currentCase && currentCase.locked">mdi-lock</v-icon>
        <template v-else>
          <v-hover>
            <v-btn
              v-show="!showFloatingSaveBtn"
              icon
              slot-scope="{ hover }"
              :color="hover ? 'accent' : ''"
              :disabled="!hasChanged"
              :loading="caseSavePending"
              @click="$emit('save')"
            >
              <v-icon>save</v-icon>
            </v-btn>
          </v-hover>
          <MenuMore offset-x offset-y>
            <v-list>
              <v-list-item color="error" @click="$emit('remove')"> Delete Case </v-list-item>
              <v-list-item @click="removeAllMessages"> Delete All Messages </v-list-item>
            </v-list>
          </MenuMore>
        </template>
      </v-toolbar-items>
    </v-toolbar>
    <v-fab-transition>
      <v-btn
        v-show="showFloatingSaveBtn && hasChanged"
        color="primary"
        fab
        small
        absolute
        right
        class="sticky-btn"
        :loading="caseSavePending"
        @click="$emit('save')"
      >
        <v-icon>save</v-icon>
      </v-btn>
    </v-fab-transition>

    <v-progress-linear
      v-if="caseLoadPending"
      class="mt-0"
      :indeterminate="true"
      color="secondary"
    ></v-progress-linear>

    <transition>
      <keep-alive>
        <component
          v-if="currentCase"
          :is="page.component"
          v-bind.sync="currentCase"
          :errors="errors"
          :num_years="num_years"
          :start-year="currentCase.settings.year_start"
          :currentCase="currentCase"
          @change="$emit('change')"
          @save="$emit('save')"
        ></component>
      </keep-alive>
    </transition>
  </div>
</template>
<script>
import debounce from "lodash/debounce";
import { mapState, mapGetters, mapActions } from "vuex";
import SystemSettings from "@/components/SystemSettings.vue";
import SystemPrograms from "@/components/SystemPrograms.vue";
import SystemReliability from "@/components/SystemReliability.vue";
import SystemDemand from "@/components/SystemDemand.vue";
import SystemGenerator from "@/components/SystemGenerator.vue";
import SystemStorage from "@/components/SystemStorage.vue";
import SystemMarket from "@/components/SystemMarket.vue";
import SystemProject from "@/components/SystemProject.vue";
import SystemNetwork from "@/components/SystemNetwork.vue";

export default {
  name: "CaseInput",
  components: {
    MenuMore: () => import("@/components/MenuMore.vue"),
    WikiButton: () => import("@/components/WikiButton.vue"),
  },
  props: {
    currentCase: Object,
    num_years: Number,
    hasChanged: Boolean,
    errors: {
      type: Object,
      required: false,
    },
    drawerState: { type: Boolean, required: false, default: true },
  },
  data() {
    return {
      newCase: null,
      settingsDialog: null,
      pages: {
        Settings: {
          name: "Settings",
          component: SystemSettings,
          wikiSection: "Settings/General%20Settings.html",
        },
        Policies: {
          name: "Policies",
          component: SystemPrograms,
          wikiSection: "Inputs/Programs.html",
        },
        ReliabilityCriteria: {
          name: "Reliability Criteria",
          component: SystemReliability,
          wikiSection: "Inputs/Reliability%20Criteria.html",
        },
        Network: {
          name: "Network",
          component: SystemNetwork,
        },
        Demand: {
          name: "Demand",
          component: SystemDemand,
          wikiSection: "Inputs/Demand.html",
        },
        Generators: {
          name: "Generators",
          component: SystemGenerator,
          wikiSection: "Inputs/Generators.html",
        },
        EnergyStorage: {
          name: "Energy Storage",
          component: SystemStorage,
          wikiSection: "Inputs/Energy%20Storage.html",
        },
        Market: {
          name: "Market",
          component: SystemMarket,
          wikiSection: "",
        },
        Project: {
          name: "Project",
          component: SystemProject,
          wikiSection: "Inputs/Project.html",
        },
      },
      showFloatingSaveBtn: false,
    };
  },
  computed: {
    ...mapState({}),
    ...mapGetters({
      caseSavePending: "cases/savePending",
      caseLoadPending: "cases/loadPending",
    }),
    hash() {
      return this.$route.hash.substring(1);
    },
    page() {
      return this.pages[this.hash] || this.pages.Settings;
    },
    toolbarTitle() {
      return this.onSettingsPage ? "Settings" : `Input: ${this.page.name}`;
    },
    onSettingsPage() {
      return this.page.name === "Settings";
    },
    wikiSection() {
      return this.page.wikiSection;
    },
  },
  watch: {},
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanged) {
      next();
      return true;
    }
    // eslint-disable-next-line no-alert
    const answer = window.confirm("Do you really want to leave? you have unsaved changes!");
    if (answer) next();
    return false;
  },
  created() {},
  mounted() {
    this.handleDebouncedScroll = debounce(this.handleScroll, 100);
    window.addEventListener("scroll", this.handleDebouncedScroll);
  },
  beforeDestroy() {
    // I switched the example from `destroyed` to `beforeDestroy`
    // to exercise your mind a bit. This lifecycle method works too.
    window.removeEventListener("scroll", this.handleDebouncedScroll);
  },
  methods: {
    ...mapActions({
      removeMessages: "messages/remove",
    }),
    removeAllMessages() {
      if (
        // eslint-disable-next-line no-alert
        window.confirm(
          "Warning: Deleted messages are non-recoverable. Are you sure you want to continue?"
        )
      ) {
        this.removeMessages([null, { query: { case_id: this.currentCase.id } }]);
      }
    },
    handleScroll(_event) {
      this.showFloatingSaveBtn = window.scrollY > 50;
    },
  },
};
</script>

<style>
.sticky-btn {
  position: fixed;
  z-index: 10;
  top: 52px;
}
</style>
